import { css } from '@emotion/core'
import { ArrowCaret, ArrowSwap } from '@fs/zion-icon/dist/cjs/icons'
import { Cell, Grid, ResponsiveTableRow, Button } from '@fs/zion-ui'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { PersonLink } from '../../app/Links'
import { getBirthDate, getBirthPlace, getDeathDate, getDeathPlace, getName, getSurname } from './PrivatePersonsUtil'
import DmcCopyToClipboardTooltip from '../../../util/DmcCopyToClipboardTooltip'

const bold = css`
  font-weight: bold;
`

export default function PrivatePersonsTable({ persons }) {
  const { t } = useTranslation()
  const [sortField, setSortField] = useState()
  const [sortType, setSortType] = useState()

  const sortPersons = () => {
    switch (sortField) {
      case 'personId': {
        switch (sortType) {
          case undefined: {
            return [...persons]
          }
          case 'ascending': {
            return [...persons].sort((a, b) => a.id.localeCompare(b.id))
          }
          case 'descending': {
            return [...persons].sort((a, b) => b.id.localeCompare(a.id))
          }
          default:
            break
        }
        break
      }
      case 'name': {
        switch (sortType) {
          case undefined: {
            return [...persons]
          }
          case 'ascending': {
            return [...persons].sort((a, b) => getSurname(a).localeCompare(getSurname(b)))
          }
          case 'descending': {
            return [...persons].sort((a, b) => getSurname(b).localeCompare(getSurname(a)))
          }
          default:
            break
        }
        break
      }
      case 'birthDate': {
        switch (sortType) {
          case undefined: {
            return [...persons]
          }
          case 'ascending': {
            return [...persons].sort((a, b) => {
              if (a.summary.lifespanBeginYear === b.summary.lifespanBeginYear) {
                return 0
              }
              if (a.summary.lifespanBeginYear === null) {
                return 1
              }
              if (b.summary.lifespanBeginYear === null) {
                return -1
              }
              return parseInt(a.summary.lifespanBeginYear, 10) - parseInt(b.summary.lifespanBeginYear, 10)
            })
          }
          case 'descending': {
            return [...persons].sort((a, b) => {
              if (b.summary.lifespanBeginYear === a.summary.lifespanBeginYear) {
                return 0
              }
              if (a.summary.lifespanBeginYear === null) {
                return 1
              }
              if (b.summary.lifespanBeginYear === null) {
                return -1
              }
              return parseInt(b.summary.lifespanBeginYear, 10) - parseInt(a.summary.lifespanBeginYear, 10)
            })
          }
          default:
            break
        }
        break
      }
      case 'birthPlace': {
        switch (sortType) {
          case undefined: {
            return [...persons]
          }
          case 'ascending': {
            return [...persons].sort((a, b) => {
              if (getBirthPlace(a) === getBirthPlace(b)) {
                return 0
              }
              if (getBirthPlace(a) === '') {
                return 1
              }
              if (getBirthPlace(b) === '') {
                return -1
              }
              return getBirthPlace(a).localeCompare(getBirthPlace(b))
            })
          }
          case 'descending': {
            return [...persons].sort((a, b) => {
              if (getBirthPlace(a) === getBirthPlace(b)) {
                return 0
              }
              if (getBirthPlace(a) === '') {
                return 1
              }
              if (getBirthPlace(b) === '') {
                return -1
              }
              return getBirthPlace(b).localeCompare(getBirthPlace(a))
            })
          }
          default:
            break
        }
        break
      }
      case 'deathDate': {
        switch (sortType) {
          case undefined: {
            return [...persons]
          }
          case 'ascending': {
            return [...persons].sort((a, b) => {
              if (a.summary.lifespanEndYear === b.summary.lifespanEndYear) {
                return 0
              }
              if (a.summary.lifespanEndYear === null) {
                return 1
              }
              if (b.summary.lifespanEndYear === null) {
                return -1
              }
              return parseInt(a.summary.lifespanEndYear, 10) - parseInt(b.summary.lifespanEndYear, 10)
            })
          }
          case 'descending': {
            return [...persons].sort((a, b) => {
              if (b.summary.lifespanEndYear === a.summary.lifespanEndYear) {
                return 0
              }
              if (a.summary.lifespanEndYear === null) {
                return 1
              }
              if (b.summary.lifespanEndYear === null) {
                return -1
              }
              return parseInt(b.summary.lifespanEndYear, 10) - parseInt(a.summary.lifespanEndYear, 10)
            })
          }
          default:
            break
        }
        break
      }
      case 'deathPlace': {
        switch (sortType) {
          case undefined: {
            return [...persons]
          }
          case 'ascending': {
            return [...persons].sort((a, b) => {
              if (getDeathPlace(a) === getDeathPlace(b)) {
                return 0
              }
              if (getDeathPlace(a) === '') {
                return 1
              }
              if (getDeathPlace(b) === '') {
                return -1
              }
              return getDeathPlace(a).localeCompare(getDeathPlace(b))
            })
          }
          case 'descending': {
            return [...persons].sort((a, b) => {
              if (getDeathPlace(a) === getDeathPlace(b)) {
                return 0
              }
              if (getDeathPlace(a) === '') {
                return 1
              }
              if (getDeathPlace(b) === '') {
                return -1
              }
              return getDeathPlace(b).localeCompare(getDeathPlace(a))
            })
          }
          default:
            break
        }
        break
      }
      default:
        break
    }
    return persons
  }

  const sortedPersons = sortPersons(sortType, sortField)
  const sortIconOnClick = (type, field) => {
    setSortType(type)
    setSortField(field)
  }

  const getFieldSortingIcon = (field) => {
    let icon
    if (field !== sortField) {
      return <Button Icon={ArrowSwap} iconDirection="down" onClick={() => sortIconOnClick('ascending', field)} />
    }
    if (sortType === undefined) {
      icon = <Button Icon={ArrowSwap} iconDirection="down" onClick={() => sortIconOnClick('ascending', field)} />
    } else if (sortType === 'ascending') {
      icon = <Button Icon={ArrowCaret} iconDirection="up" onClick={() => sortIconOnClick('descending', field)} />
    } else if (sortType === 'descending') {
      icon = <Button Icon={ArrowCaret} iconDirection="down" onClick={() => sortIconOnClick(undefined, field)} />
    }
    return icon
  }

  return (
    <>
      <Grid>
        <Cell columns={2}>
          <span css={bold}>
            {t('personId', 'Person ID')} {getFieldSortingIcon('personId')}
          </span>
        </Cell>
        <Cell columns={2}>
          <span css={bold}>
            {t('name', 'Name')} {getFieldSortingIcon('name')}
          </span>
        </Cell>
        <Cell columns={2}>
          <span css={bold}>
            {t('user.privatePersons.birthDate', 'Birth Date')} {getFieldSortingIcon('birthDate')}
          </span>
        </Cell>
        <Cell columns={2}>
          <span css={bold}>
            {t('birthPlace', 'Birth Place')} {getFieldSortingIcon('birthPlace')}
          </span>
        </Cell>
        <Cell columns={2}>
          <span css={bold}>
            {t('deathDate', 'Death Date')} {getFieldSortingIcon('deathDate')}
          </span>
        </Cell>
        <Cell columns={2}>
          <span css={bold}>
            {t('deathPlace', 'Death Place')} {getFieldSortingIcon('deathPlace')}
          </span>
        </Cell>
      </Grid>
      <DmcResponsiveTable
        id="privatePersonsTable"
        totalColumnCount={6}
        atSizeColumns={{
          default: {
            labels: ['', ' ', '  ', '   ', '    ', '     '],
          },
        }}
      >
        {sortedPersons.map((person) => {
          return (
            <ResponsiveTableRow
              key={person.id}
              column1={
                <DmcCopyToClipboardTooltip
                  nextToText
                  value={person.id}
                  body={<PersonLink personId={person.id} newTab />}
                />
              }
              column2={getName(person)}
              column3={getBirthDate(person)}
              column4={getBirthPlace(person)}
              column5={getDeathDate(person, t)}
              column6={getDeathPlace(person)}
            />
          )
        })}
      </DmcResponsiveTable>
    </>
  )
}
